import {
    LayoutDashboard, Users, CalendarDays, CalendarClock, MessageSquareWarning, GraduationCap, BookOpen, Boxes,
    HeartHandshake, TrendingUp, ShieldCheck, ClipboardCheck, Camera, Wrench, Car, Lightbulb,
    Zap, Banknote, ShoppingBag, FolderOpen, Trophy, Share2, Bell, MessageCircle,
    Settings as SettingsIcon, UserCircle2, Database, Store, ChefHat, Building2, Briefcase, Circle, Truck,
    Activity, BarChart3, BoxSelect, Calculator, ClipboardList, FileText,
    Landmark, Package, Receipt, Wallet,
    Calendar, Edit2, LayoutTemplate, UserX, ListChecks, ShieldAlert, SlidersHorizontal, Gauge,
    Play, Film, Megaphone, Grid3x3, Inbox, CheckSquare, BookMarked, MapPinned, Plug, Server, Globe, PartyPopper,
} from 'lucide-react';
import katalog from './moduly.json';

/**
 * Katalog modulů OS — jediný zdroj pravdy pro menu, mobilní menu, hledání,
 * hlavičku stránky i editor profilů na backendu (`App\Services\KatalogModulu`
 * čte stejný `moduly.json`).
 *
 * Tady se k datům jen přidávají ikony (JSON je sdílený s PHP, komponenty
 * v něm být nemůžou). Nový modul = záznam v `moduly.json` + ikona níž.
 *
 * `id` modulů jsou zároveň kódy oprávnění — neměnit. `podstranky` jsou
 * záložky modulu; přístup se nastavuje na ně (klíč `modul.podstranka`, u modulu
 * bez podstránek kód modulu — viz App\Services\Opravneni).
 * `stranky` jsou další stránky (`currentPage`) modulu (detail, editor…) → co
 * vyžadují: id podstránky, `"*"` = jakákoli podstránka modulu, `null` = osobní
 * stránka bez kontroly (např. detail vlastního školení). Menu i hlavička podle
 * nich ví, kde uživatel je; brána v app.jsx podle nich pouští dovnitř.
 */

const IKONY_SEKCI = {
    tym: Users,
    obchod: Store,
    produkt: ChefHat,
    provoz: Building2,
    rizeni: Briefcase,
};

const IKONY_MODULU = {
    zpravy: MessageCircle,
    znalosti: BookMarked,
    zamestnanci: Users,
    smeny: CalendarDays,
    objednavky: ShoppingBag,
    zakaznici: HeartHandshake,
    social: Share2,
    web: Globe,
    'produkt-kvalita': BookOpen,
    balirna: Boxes,
    checklisty: ClipboardCheck,
    kamery: Camera,
    rozvoz: Car,
    udrzba: Wrench,
    dashboard: TrendingUp,
    finance: Banknote,
    podnety: Lightbulb,
    sprava: SettingsIcon,
};

/**
 * Ikony podstránek (záložek) — klíč `modul.podstranka`.
 *
 * Stejný důvod jako u `IKONY_MODULU`: JSON je sdílený s PHP, komponenty v něm
 * být nemůžou. Název, pořadí a přístup k záložce drží katalog, ikonu tahle
 * mapa. Co tu není, dostane tečku — záložka bez ikony je pořád záložka.
 */
const IKONY_PODSTRANEK = {
    // Web
    'web.akce': PartyPopper,
    // Komunikace
    'zpravy.zpravy': MessageCircle,
    'zpravy.nastenka': Megaphone,
    'zpravy.zadosti': Inbox,
    'zpravy.ukoly': CheckSquare,
    // Znalosti
    'znalosti.jednobodovky': ListChecks,
    'znalosti.navody': FileText,
    'znalosti.smernice': ShieldCheck,
    'znalosti.zarizeni': Wrench,
    // Finance
    'finance.vysledovka': BarChart3,
    'finance.doklady': FileText,
    'finance.dodavatele': Truck,
    'finance.banka': Landmark,
    'finance.pokladna': Wallet,
    'finance.aop': Calculator,
    'finance.dph': Receipt,
    // Správa
    'sprava.uzivatele': UserCircle2,
    'sprava.provozovna': Store,
    'sprava.firma': Landmark,
    'sprava.integrace': Plug,
    'sprava.system': Server,
    // Flotila
    'rozvoz.live': Activity,
    'rozvoz.zony': MapPinned,
    'rozvoz.ridici': Users,
    'rozvoz.auta': Car,
    // Balírna
    'balirna.obaly': BoxSelect,
    'balirna.predpisy': ClipboardList,
    'balirna.spotrebni': ShoppingBag,
    // Objednávky (Hosté 2026-10-06 až 10-12; od 10-12 bez Zákazníků)
    'objednavky.zive': ShoppingBag,
    'objednavky.rezervace': CalendarClock,
    'objednavky.vyrizeni': MessageSquareWarning,
    // Zákazníci (samostatný modul od 2026-10-12)
    'zakaznici.seznam': Users,
    'zakaznici.komunikace': MessageCircle,
    'zakaznici.vernost': Trophy,
    'zakaznici.kampane': Megaphone,
    // Lidé (HR modul — od 2026-09-28 i bývalé Vzdělávání a Hodnocení)
    'zamestnanci.seznam': Users,
    'zamestnanci.dokumenty': FolderOpen,
    'zamestnanci.kvalifikace': Grid3x3,
    'zamestnanci.skoleni': GraduationCap,
    'zamestnanci.kariery': Trophy,
    'zamestnanci.hodnoceni': Trophy,
    // Směny
    'smeny.plan': Calendar,
    'smeny.sablony': LayoutTemplate,
    'smeny.dochazka': ClipboardCheck,
    'smeny.nepritomnost': UserX,
    // Checklisty
    'checklisty.prehled': Gauge,
    'checklisty.historie': ListChecks,
    'checklisty.parametry': BarChart3,
    'checklisty.sablony': SlidersHorizontal,
    // Kamery
    'kamery.zive': Play,
    'kamery.zaznamy': Film,
    'kamery.sprava': SettingsIcon,
    // Social Media
    'social.planovac': CalendarDays,
    'social.generator': Film,
    'social.kampane': Megaphone,
    'social.analytika': BarChart3,
    // KPI Dashboard
    'dashboard.trzby': BarChart3,
    'dashboard.energie': Zap,
    'dashboard.kvalita': ShieldCheck,
    'dashboard.doruceni': Truck,
};

/**
 * Sekce menu s moduly (tvar `{ id, label, popis, color, colorSvetly, colorBg, Icon, items }`).
 *
 * Každá sekce má **dva tóny téže barvy**. `color` je identita sekce — sytá,
 * dělaná pro tmavý motiv a pro podbarvení (`colorBg`). `colorSvetly` je tentýž
 * odstín ztmavený tak, aby unesl text na světlém podkladu: ve světlém motivu
 * měla původní pětice na mléčném skle kontrast 2,1–3,5 : 1, tedy pod hranicí
 * čitelnosti i pro velký text. Vybírá mezi nimi `barvaSekce(sekce, isDark)`
 * — pro **popředí** (text, ikona), zatímco podbarvení zůstává ze sytého tónu,
 * jinak by zešedlo.
 */
export const SEKCE = katalog.sekce.map((s) => ({
    id: s.id,
    label: s.nazev,
    popis: s.popis,
    color: s.barva,
    colorSvetly: s.barvaSvetla || s.barva,
    colorBg: `${s.barva}1f`,
    Icon: IKONY_SEKCI[s.id] || Circle,
    items: s.moduly.map((m) => ({
        id: m.id,
        label: m.nazev,
        popis: m.popis,
        // Modul, který není k přidělení — vidí ho každý přihlášený.
        proVsechny: !!m.proVsechny,
        // Modul s vlastním slotem v mobilní liště (Zprávy). Do mřížky modulů
        // se nekreslí — byl by tam podruhé. Desktopu se to netýká: tam ho
        // lišta i hledání ukazují dál jako každý jiný modul.
        slotVListe: !!m.slotVListe,
        // Modul, který se teprve staví. V menu zůstane (kdo ho má přidělený,
        // ať ví, že přijde), ale ztlumený a bez prokliku — jinak do něj člověk
        // klepne a najde prázdnou stránku.
        pripravovany: !!m.pripravovany,
        stranky: m.stranky || {},
        podstranky: m.podstranky || [],
        Icon: IKONY_MODULU[m.id] || Circle,
    })),
}));

// Stránky mimo moduly (osobní, systémové utility) — pro hlavičku na mobilu
// a hledání. Nejsou to oprávnění.
// Zprávy tu nejsou: staly se modulem v Týmu. Dvojí záznam by je do hledání
// dostal dvakrát a `findPageMeta` by stejně dal přednost modulu.
export const SPECIAL_PAGES = {
    prehled:    { label: 'Přehled',         Icon: LayoutDashboard, color: '#FF8A00', colorSvetly: '#AD5D00', colorBg: 'rgba(255,138,0,0.12)' },
    aktivita:   { label: 'Aktivita',        Icon: Bell,            color: '#FF8A00', colorSvetly: '#AD5D00', colorBg: 'rgba(255,138,0,0.12)' },
    // Rozvozový cockpit se otevírá z Mojí směny a modulem není. Bez záznamu
    // vracel `findPageMeta` null a střed mobilní lišty zůstal prázdný —
    // uprostřed toho, co řidič dělá celou směnu.
    'rozvoz-cockpit': { label: 'Rozvoz', Icon: Truck, color: '#FF8A00', colorSvetly: '#AD5D00', colorBg: 'rgba(255,138,0,0.12)' },
    // Znalosti ze spodní lišty (stránka pro všechny, ne modul Znalosti)
    'znalosti-seznam': { label: 'Znalosti', Icon: BookMarked, color: '#FF8A00', colorSvetly: '#AD5D00', colorBg: 'rgba(255,138,0,0.12)' },
    profil:     { label: 'Profil',          Icon: UserCircle2,     color: '#6B7280', colorBg: 'rgba(107,114,128,0.12)' },
    media:      { label: 'Správce souborů', Icon: FolderOpen,      color: '#6B7280', colorBg: 'rgba(107,114,128,0.12)' },
    'kds-test': { label: 'KDS Test',        Icon: Database,        color: '#6B7280', colorBg: 'rgba(107,114,128,0.12)' },
};

// Kolik modulů se ještě vejde na lištu jako samostatné ikony. Kdo jich má víc,
// dostane ikony sekcí s vyskakovacím panelem.
export const MAX_IKON_MODULU = 6;

/** Modul (a jeho sekce), ke kterému stránka patří — podle `id` nebo aliasu. */
export function modulStranky(pageId) {
    if (!pageId) return null;
    for (const sekce of SEKCE) {
        const item = sekce.items.find((i) => i.id === pageId || pageId in i.stranky);
        if (item) return { item, sekce };
    }
    return null;
}

/** Metadata stránky (label, Icon, color, colorBg) pro hlavičku. */
export function findPageMeta(pageId) {
    const nalez = modulStranky(pageId);
    if (nalez) return {
        ...nalez.item,
        color: nalez.sekce.color,
        colorSvetly: nalez.sekce.colorSvetly,
        colorBg: nalez.sekce.colorBg,
    };
    return SPECIAL_PAGES[pageId] || null;
}

/**
 * Barva sekce pro **popředí** podle motivu — text a ikony, které leží přímo
 * na ploše nebo na skle. Na podbarvení (`colorBg`) se nepoužívá: tam má být
 * sytý tón, ztmavený by ho zašpinil.
 */
export function barvaSekce(sekce, isDark) {
    if (!sekce) return undefined;
    return isDark ? sekce.color : (sekce.colorSvetly || sekce.color);
}


/**
 * Vidí uživatel modul? Superadmin vidí vše, ostatní podle výsledných přístupů
 * (šablona + výjimky) — `/auth/me` vrací `moduly`, ke kterým má aspoň jednu
 * podstránku.
 */
export function vidiModul(user, modulId) {
    if (user?.role === 'superadmin') return true;
    // Moduly bez přístupů (Zprávy) vidí každý. Bez téhle výjimky by zmizely
    // všem kromě superadmina — backend je v seznamu `moduly` nemá a mít nebude.
    if (SEKCE.some((s) => s.items.some((i) => i.id === modulId && i.proVsechny))) return true;
    return (user?.moduly || user?.profil?.moduly || []).includes(modulId);
}

/**
 * Má uživatel přístup ke klíči (`smeny.plan`, u modulu bez podstránek kód
 * modulu) aspoň na úrovni? Úrovně `vidi` < `upravuje`. Superadmin vždy.
 */
export function muze(user, klic, uroven = 'vidi') {
    if (user?.role === 'superadmin') return true;
    const u = user?.opravneni?.[klic];
    if (!u) return false;
    return uroven === 'vidi' || u === 'upravuje';
}

/** Podstránky (záložky) modulu z katalogu — v pořadí, které v něm mají. */
export function podstrankyModulu(modulId) {
    const item = SEKCE.flatMap((s) => s.items).find((i) => i.id === modulId);
    return item ? item.podstranky : [];
}

/**
 * Záložky modulu, které uživatel vidí — název, pořadí a ikona z katalogu,
 * filtr podle přístupů (`modul.podstranka`).
 *
 * Tenhle řádek byl slovo od slova v osmi stránkách. Kreslí z něj `TabBar`;
 * stránka ho potřebuje sama jen tam, kde se záložkami dělá ještě něco jiného
 * (přejetí prstem mezi nimi).
 */
export function zalozkyModulu(modulId, user) {
    return podstrankyModulu(modulId)
        // `bezZalozky` = jen přístup, ne obrazovka. Historie objednávek odemyká
        // delší období v záložce Objednávky, vlastní záložku nemá.
        .filter((p) => !p.bezZalozky)
        // `proVsechny` = záložka bez přístupového klíče (Komunikace — vidí každý)
        .filter((p) => p.proVsechny || muze(user, `${modulId}.${p.id}`))
        .map((p) => ({
            id: p.id,
            nazev: p.nazev,
            Icon: ikonaPodstranky(modulId, p.id),
            // Záložka, která se teprve staví (Hodnocení v Lidech). Kreslí se
            // ztlumená se štítkem — kdo ji má přidělenou, ví, že přijde.
            pripravovany: !!p.pripravovany,
        }));
}

/** Ikona záložky podle klíče `modul.podstranka`. */
export function ikonaPodstranky(modulId, podstrankaId) {
    return IKONY_PODSTRANEK[`${modulId}.${podstrankaId}`] || Circle;
}

/** Klíče přístupů modulu — podstránky, nebo kód modulu, když žádné nemá. */
export function klicePristupu(modulId) {
    const item = SEKCE.flatMap((s) => s.items).find((i) => i.id === modulId);
    if (!item) return [];
    const sKlicem = item.podstranky.filter((p) => !p.proVsechny);
    return sKlicem.length ? sKlicem.map((p) => `${item.id}.${p.id}`) : [item.id];
}

/**
 * Stránky mimo katalog, které přesto nejsou pro každého.
 *
 * `smiNaStranku` je pouští, protože nepatří žádnému modulu — a do zavedení
 * adresy v hashi to nevadilo: nešlo se na ně dostat odjinud než z míst, která
 * si roli hlídají sama (Nastavení jen adminovi). Jakmile stav navigace bydlí
 * v adrese, otevře je každý, kdo ji napíše, takže si o roli řeknou tady.
 *
 * Osobní stránky mimo katalog (Přehled, Aktivita, Profil, rozvozový cockpit)
 * sem nepatří — ty patří každému a jejich data chrání API.
 */
const ROLE_STRANEK = {
    media:       ['superadmin', 'admin'],
    'kds-test':  ['superadmin'],
};

/**
 * Smí uživatel otevřít stránku? Stránky mimo moduly (Přehled, Zprávy…) ano,
 * modul podle přístupů, další stránky modulu podle `stranky` v katalogu.
 */
export function smiNaStranku(user, pageId) {
    const role = ROLE_STRANEK[pageId];
    if (role) return role.includes(user?.role);
    const nalez = modulStranky(pageId);
    if (!nalez) return true;
    const { item } = nalez;
    if (pageId === item.id) return vidiModul(user, item.id);
    const pozadavek = item.stranky[pageId];
    if (pozadavek === null) return true;
    if (pozadavek === '*') return vidiModul(user, item.id);
    return muze(user, `${item.id}.${pozadavek}`);
}

/** Sekce jen s moduly, které uživatel vidí; prázdné sekce vypadnou. */
export function viditelneSekce(user) {
    return SEKCE
        .map((s) => ({ ...s, items: s.items.filter((i) => vidiModul(user, i.id)) }))
        .filter((s) => s.items.length > 0);
}
